import PropTypes from 'prop-types';
import { useState } from 'react';

import { ContainerProps as MuiContainerProps } from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import { EmailSignin } from '@classity-edtech/ui-auth';
import { Roles, FreeFormName, FormStatus } from '@classity-edtech/platform-utils';
import {
  AUTH_KEY,
  formatError,
  getCookieValue,
  setAuthCookie,
  windowExists,
} from '@classity-edtech/ui-base';
import { useAppDispatch } from '../../store/hooks';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useLoginMutation } from '../../services/authentication';
import { webPaths } from '../../routes/paths';
import { updateFlashMessage } from '../../store/actions/flashMessage';
import GoogleButton from '../../components/GoogleButton';
import { useLazyGetMyProfileQuery } from '../../services/users';
import { useLazyGetResponsesByFormNameQuery } from '../../services/response';
import { useConfig } from '../../contexts';

export interface SigninProps extends MuiContainerProps {}

export const Signin = ({ className }: SigninProps) => {
  const dispatch: any = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isAccountActivated = searchParams.get('isAccountActivated') === 'true';
  const isPasswordRenewed = searchParams.get('isPasswordRenewed') === 'true';
  const previousUrl = searchParams.get('previousUrl');
  const [signin] = useLoginMutation();
  const [getUser] = useLazyGetMyProfileQuery();
  const { learnUi } = useConfig() as any;
  const learnUiUrl = learnUi?.url;
  const [page] = useState(1);
  const limit = 1;

  const [getApplications, { data: applications }] = useLazyGetResponsesByFormNameQuery();

  const handleNavigation = (path: string) => {
    if (path === webPaths.root) {
      navigate(`/`);
    }

    navigate(`/${path}`);
  };

  const handleAuthAction = async (role: string) => {
    if (previousUrl) return previousUrl;

    if (role === Roles.STUDENT) {
      const user = await getUser();
      const profile: Record<string, any> | undefined = user?.data?.profile;

      // Check if the learner has completed the onboarding steps (profile and country selection).
      // If not, redirect them to the appropriate page.
      if (!profile?.ids) {
        return `${learnUiUrl}/student/profile`;
      }

      return `${learnUiUrl}/s`;
    }

    return `${learnUiUrl}/`;
  };

  const handleConfirm = async (payload: { email: string; password: string }) => {
    const existingClientId = getCookieValue(AUTH_KEY);

    if (existingClientId) {
      const user = await getUser();

      if (user?.data?.roleId) {
        const url = await handleAuthAction(user.data.roleId);
        return ((windowExists as any).location = url);
      }
    }

    const signinResultData = await signin(payload);

    if ('error' in signinResultData) {
      const error = formatError(signinResultData.error.message)?.message || 'An error occurred!';
      return dispatch(updateFlashMessage({ message: error, severity: 'error', open: true }));
    }

    if ('data' in signinResultData) {
      setAuthCookie(signinResultData?.data);

      const user = await getUser();

      const url = await handleAuthAction(user?.data?.roleId);

      return ((windowExists as any).location = url);
    }
  };

  return (
    <EmailSignin
      navigate={handleNavigation}
      authAction={handleAuthAction}
      isAccountActivated={isAccountActivated}
      isPasswordRenewed={isPasswordRenewed}
      signinAction={handleConfirm}
      googleBtn={<GoogleButton text='signin_with' />}
    />
  );
};

Signin.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

Signin.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(Signin)(
  ({ theme }) => `
`,
);
